import React, { type MutableRefObject } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import DetailViewIcon from '@atlaskit/icon/glyph/detail-view';
import EmailIcon from '@atlaskit/icon/glyph/email';
import LinkIcon from '@atlaskit/icon/glyph/link';
import Modal, {
	ModalTransition,
	useModal,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import { Box, Stack, Inline, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import headerImage from './assets/jsmModalHeader.png';
import messages from './messages';

export const jsmFeaturesBulletPoints = [
	{
		icon: <EmailIcon label="email icon" />,
		content: messages.captureRequests,
	},
	{
		icon: <DetailViewIcon label="detail view icon" />,
		content: messages.priortiseWorkUsingQueues,
	},
	{
		icon: <LinkIcon label="link icon" />,
		content: messages.linkIssuesToJira,
	},
];

export interface CustomModalContentProps {
	isLoading: boolean;
}

const CustomModalContent = ({ isLoading }: CustomModalContentProps) => {
	const { onClose } = useModal();
	const { formatMessage } = useIntl();

	return (
		<Stack xcss={CustomModalContentContainerStyles}>
			<HeaderImage alt={formatMessage(messages.altImageText)} src={headerImage} />
			<Box xcss={CloseModalButtonWrapperStyles}>
				<IconButton onClick={onClose} appearance="subtle" icon={CrossIcon} label="Close modal" />
			</Box>
			<Stack xcss={CustomModalTextContentContainerStyles} space="space.200">
				<Box paddingBlockStart="space.200" paddingBlockEnd="space.200">
					<Heading size="medium">{formatMessage(messages.creatingServiceProject)}</Heading>
				</Box>
				{isLoading && <Spinner />}
				<Stack xcss={JSMUpdatesBulletPointsStyles} space="space.100">
					{jsmFeaturesBulletPoints.map((jsmFeaturesBulletPoint, index) => (
						<Inline key={index}>
							{jsmFeaturesBulletPoint.icon}
							<>&nbsp; {formatMessage(jsmFeaturesBulletPoint.content)}</>
						</Inline>
					))}
				</Stack>
			</Stack>
		</Stack>
	);
};

export interface AutoCreateProjectModalProps {
	isModalOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
	isLoading: boolean;
	isModalOpenRef: MutableRefObject<boolean>;
}

const AutoCreateProjectModal = ({
	isModalOpen,
	setIsOpen,
	isLoading,
	isModalOpenRef,
}: AutoCreateProjectModalProps) => {
	const closeModal = () => {
		if (setIsOpen) {
			setIsOpen(false);
			// eslint-disable-next-line no-param-reassign
			isModalOpenRef.current = false;
		}
	};

	return (
		<ModalTransition>
			{isModalOpen && (
				<ShortcutScope>
					<Modal
						onClose={(e: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'modal closed', 'autoCreateProjectModal');
							closeModal();
						}}
						width={600}
						testId="global-pages.directories.projects-directory-v3.ui.page-layout.top-banner.auto-create-project-modal.auto-create-project-modal"
					>
						<CustomModalContent isLoading={isLoading} />
					</Modal>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};

export default AutoCreateProjectModal;

const CustomModalContentContainerStyles = xcss({
	position: 'relative',
	width: '100%',
	height: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderImage = styled.img({
	borderRadius: `${token('border.radius', '3px')} ${token('border.radius', '3px')} 0 0`,
});

const CustomModalTextContentContainerStyles = xcss({
	padding: 'space.300',
});

const JSMUpdatesBulletPointsStyles = xcss({
	marginTop: 'space.200',
	textAlign: 'left',
});

const CloseModalButtonWrapperStyles = xcss({
	position: 'absolute',
	top: '24px',
	right: '24px',
	padding: 'space.0',
});
